import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { User, Users, Building2, Loader2, ShieldCheck } from "lucide-react";

const ROLES = [
  { value: "model", label: "Model", icon: User, desc: "Licence your own look and earn passively" },
  { value: "agency", label: "Agency", icon: Users, desc: "Manage and licence a roster of models" },
  { value: "client", label: "Client", icon: Building2, desc: "Book real looks for your brand imagery" },
];

export default function ChooseRole() {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [role, setRole] = useState("model");
  const [busy, setBusy] = useState(false);

  if (!loading && user?.role) return <Navigate to="/dashboard" replace />;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/set-role", { role });
      login(data.token, data.user);
      toast.success(`Welcome to LOOKBOOK, ${data.user.name}`);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, "Could not set your role"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-mist min-h-[calc(100vh-4rem)]">
      <div className="mx-auto flex max-w-lg flex-col gap-6 px-4 py-14">
        <div className="text-center">
          <h1 className="font-display text-3xl tracking-[-0.01em]">One last step</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            How will you use LOOKBOOK{user?.name ? `, ${user.name.split(" ")[0]}` : ""}? This choice is permanent —
            it defines your dashboard, permissions and agreements.
          </p>
        </div>
        <Card className="rounded-2xl shadow-[0_10px_40px_rgba(30,26,22,0.08)]">
          <CardContent className="space-y-4 p-6">
            <div className="grid gap-3">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  data-testid={`choose-role-${r.value}`}
                  onClick={() => setRole(r.value)}
                  className={`flex items-center gap-4 rounded-xl border p-4 text-left transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    role === r.value
                      ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]"
                      : "bg-card hover:bg-muted"
                  }`}
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                    role === r.value ? "bg-[hsl(174,38%,34%)] text-white" : "bg-muted text-muted-foreground"
                  }`}>
                    <r.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{r.label}</span>
                    <span className="block text-xs text-muted-foreground">{r.desc}</span>
                  </span>
                </button>
              ))}
            </div>
            <Button data-testid="choose-role-confirm-button" disabled={busy} onClick={confirm} className="w-full rounded-xl">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Continue as {ROLES.find((r) => r.value === role)?.label}
            </Button>
            <p className="flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
              <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(174,38%,34%)]" />
              Models and agencies must still pass the strict onboarding pipeline (ID scan + photo dataset) before
              appearing in the marketplace.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
